import {Box} from '@viptech/react-components'
import moment from 'moment'
import {useEffect, useState} from 'react'
import {FilterSelectedFinal} from 'src/components/advancedFilters/AdvancedFilters'
import InfiniteScrollLoading from 'src/components/loading/InfiniteScrollLoading'
import {useCompany} from 'src/contexts/CompanyContext'
import useCameras from 'src/hooks/api/camera/useCameras'
import useFaceDetectionsMosaic from 'src/hooks/api/facial/useFaceDetectionsMosaic'
import useDebounce from 'src/hooks/useDebounce'
import {useCompaniesSelector} from 'src/layouts/main-layout/companies-selector/companies-selector-context'
import {FaceDetection} from 'src/services/api'
import createStyleSheet from 'src/utilities/createStyleSheet'
import {FilterDate} from '../alarms/AlarmsFunctions'
import AddFaceModal from './components/AddFaceModal'
import FaceBindingModal from './components/FaceBindingModal'
import FaceDetailsModal from './components/FaceDetailsModal'
import {FacialRecognitionHeader} from './components/FacialRecognitionHeader'
import NoneFaceDetected from './components/NoFaceDetection'
import {PhotoEmphasis} from './components/PhotoEmphasis'
import {PhotoEmphasisSkeletonMap} from './components/PhotoEmphasisSkeleton'
import {PhotoFrame} from './components/PhotoFrame'
import {PhotoFrameSkeletonMap} from './components/PhotoFrameSkeleton'

export type FacesFilter = {
  id?: number
  cameraClientId?: Array<number>
  clientIds?: Array<number>
  companyIds: Array<number>
  faceName?: string | null
  listId?: Array<number>
}

export type FaceDetectionGlobalSearch = {
  cameraName: string
  faceName: string
}

const START_DATE = moment(new Date().setHours(0, 0, 0)).format()
const FINAL_DATE = moment().endOf('day').format()

export default function FacialRecognition() {
  const {companies} = useCompany()
  const {selectedCompanies} = useCompaniesSelector()

  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const [filterDate, setFilterDate] = useState<FilterDate>({
    from: START_DATE,
    to: FINAL_DATE,
  })

  const isFilteringPastDate = () => {
    const withoutMiliTo = new Date(filterDate.to ?? '').setMilliseconds(0)
    const withoutMiliNow = new Date().setMilliseconds(0)
    return withoutMiliTo < withoutMiliNow
  }

  useEffect(() => {
    if (isFilteringPastDate()) websocket.forceDisconnect()
  }, [filterDate.to, filterDate.from])

  const [chosenFilters, setChosenFilters] = useState<Array<FilterSelectedFinal>>([])
  const [filter, setFilter] = useState<FacesFilter>({companyIds: selectedCompanies.map((it) => it.id)})
  const {data: cameras} = useCameras({selectedCompanies: selectedCompanies.map((it) => it.id), originsToFilter: ['RTSP', 'RTSP-DVR', 'RTMP']})

  const {data, hasNextPage, isFetchingNextPage, fetchNextPage, emphasisFaceDetections, isLoading, isRefetching, websocket} = useFaceDetectionsMosaic({
    filters: filter,
    search: debouncedSearchTerm,
    start: filterDate.from ? filterDate.from : START_DATE,
    end: filterDate.to ? filterDate.to : FINAL_DATE,
  })

  const [faceDetectionSelected, setFaceDetectionSelected] = useState<FaceDetection | null>(null)
  const [modalOpen, setModalOpen] = useState<'addFace' | 'faceDetails' | 'bindFace'>()

  const onChangeFilter = (filter: FacesFilter) => {
    setFilter(filter)
  }

  const onPressBackToLive = async () => {
    setFilter({companyIds: selectedCompanies.map((it) => it.id)})
    setFilterDate({from: START_DATE, to: FINAL_DATE})
    setChosenFilters([])
    setSearchTerm('')
    websocket.forceConnect()
  }

  const openModal = (faceDetection: FaceDetection) => {
    setModalOpen('faceDetails')
    setFaceDetectionSelected(faceDetection)
  }

  const closeModal = () => {
    setFaceDetectionSelected(null)
    setModalOpen(undefined)
  }

  const findCameraName = (cameraId: number) => {
    return cameras?.data.data.entities.find((camera) => camera.id === cameraId)?.name
  }

  const findCompanyName = (companyId: number) => {
    return companies.find((company) => company.id === companyId)?.name
  }

  useEffect(() => {
    setFilter((prev) => ({...prev, companyIds: selectedCompanies.map((it) => it.id)}))
  }, [selectedCompanies])


  const [debug, setDebug] = useState(false)
  async function handleKeyPress(event: any) {
    if (event.key === 'd') {
      setDebug(true)
    }
  }
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress)
  }, [])

  return (
    <Box display='flex' flex={1} overflow='hidden' backgroundColor='white' padding='20px' borderRadius='8px' maxHeight='83vh' minHeight='83vh'>
      <Box display='flex' flex={1} direction='column' rowGap='32px'>
        <FacialRecognitionHeader
          filter={filter}
          filterDate={filterDate}
          cameras={cameras?.data.data.entities}
          chosenFilters={chosenFilters}
          setChosenFilters={setChosenFilters}
          onChangeFilter={onChangeFilter}
          search={searchTerm}
          onSearch={setSearchTerm}
          setFilterDate={setFilterDate}
          webSocketConnectionStatus={websocket.webSocketConnectionStatus}
          onPressBackToLive={onPressBackToLive}
          onPressDisconnect={websocket.forceDisconnect}
          forceConnetionSocket={websocket.forceConnect}
          isFilteringPastDate={isFilteringPastDate}
        />

        {isLoading ? (
          <Box style={styles.grid} marginRight='14px'>
            <PhotoFrameSkeletonMap />
          </Box>
        ) : data && data.length ? (
          <Box
            style={styles.grid}
            marginRight='14px'
            onScroll={(e) => {
              const target = e.target as HTMLElement
              const GAP = 10
              const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight <= GAP
              if (nearBottom && hasNextPage) fetchNextPage()
            }}>
            {data.map((item) => (
              <PhotoFrame key={item.id} onClick={openModal} faceDetection={item} cameraName={findCameraName(item.cameraId)} debug={debug} />
            ))}
            <InfiniteScrollLoading isLoading={isFetchingNextPage} />
          </Box>
        ) : (
          <NoneFaceDetected />
        )}
      </Box>

      {isLoading ? (
        <PhotoEmphasisSkeletonMap display={!isLoading && !isRefetching && !emphasisFaceDetections?.length ? 'none' : 'flex'} />
      ) : emphasisFaceDetections && emphasisFaceDetections.length ? (
        <Box
          width='232px'
          minWidth='232px'
          display={!isLoading && !isRefetching && !emphasisFaceDetections?.length ? 'none' : 'flex'}
          rowGap='16px'
          direction='column'
          overflow='auto'
          style={styles.overflow}>
          {emphasisFaceDetections?.map((item) => (
            <PhotoEmphasis key={item.id} faceDetection={item} onClick={openModal} cameraName={findCameraName(item.cameraId)} />
          ))}
        </Box>
      ) : (
        <></>
      )}

      {faceDetectionSelected && modalOpen === 'faceDetails' ? (
        <FaceDetailsModal
          faceDetectionSelected={faceDetectionSelected}
          closeModal={closeModal}
          companyName={findCompanyName(faceDetectionSelected.companyId)}
          cameraName={findCameraName(faceDetectionSelected.cameraId)}
          onAddFace={() => setModalOpen('addFace')}
          onBindFace={() => setModalOpen('bindFace')}
          onChangeFaceDetection={setFaceDetectionSelected}
        />
      ) : modalOpen === 'bindFace' && faceDetectionSelected ? (
        <FaceBindingModal
          onReturn={() => setModalOpen('faceDetails')}
          closeModal={closeModal}
          faceDetection={faceDetectionSelected}
          cameraName={findCameraName(faceDetectionSelected.cameraId)}
          companyName={findCompanyName(faceDetectionSelected.companyId)}
        />
      ) : (
        <AddFaceModal faceDetectionSelected={faceDetectionSelected} closeModal={closeModal} onReturn={() => setModalOpen('faceDetails')} />
      )}
    </Box>
  )
}

const styles = createStyleSheet({
  overflow: {
    overflow: 'auto',
    scrollbarWidth: 'none',
    '-ms-overflow-style': 'none',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))',
    overflow: 'auto',
    rowGap: '24px',
    columnGap: '16px',
    scrollbarWidth: 'none',
    '-ms-overflow-style': 'none',
  },
})
